import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const BUILDING = [
    "Healthcare ecosystem conversations",
    "Hospital partnership development",
    "Patient journey validation",
    "Technology architecture",
    "AI product development",
    "International market research",
];

export default function AboutPage() {
    usePageMeta(
        "GoMedVoyage | Cross-Border Healthcare Platform",
        "GoMedVoyage is an India-based healthcare technology company building an AI-powered cross-border healthcare platform. Built from India, designed for the world."
    );

    return (
        <div data-testid="about-page">
            <Header />
            <main>
                <section className="pt-40 pb-24 bg-gradient-to-b from-tint-blue to-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <Reveal>
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-green">About GoMedVoyage</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 data-testid="about-headline" className="mt-6 max-w-4xl font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-brand-blue">
                                Built from India. <span className="text-brand-green">Designed for the world.</span>
                            </h1>
                        </Reveal>
                    </div>
                </section>

                <section className="py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
                        <div className="lg:col-span-7 space-y-8">
                            <Reveal>
                                <p data-testid="about-belief" className="text-lg leading-relaxed text-slate-700">
                                    GoMedVoyage was founded with a simple belief: accessing healthcare across borders should
                                    not require patients and families to navigate a maze of disconnected providers, paperwork,
                                    communication channels and travel arrangements.
                                </p>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="text-base leading-relaxed text-slate-600">
                                    We are bringing together healthcare expertise, technology, artificial intelligence and human
                                    coordination to create a simpler and more trusted international healthcare experience.
                                </p>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="text-base leading-relaxed text-slate-600">
                                    Our long-term vision is to create an intelligent healthcare infrastructure connecting
                                    patients, healthcare providers, technology and trusted partners across borders — moving
                                    from medical tourism, to healthcare navigation, to an AI-powered patient journey, to a
                                    true International Patient Operating System.
                                </p>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <div className="rounded-3xl border border-slate-100 bg-tint-slate p-8">
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Where we are today</p>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                        GoMedVoyage is currently in its early validation and ecosystem-building phase. We believe
                                        transparency builds more trust than pretending otherwise.
                                    </p>
                                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {BUILDING.map((b) => (
                                            <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                                                <CheckCircle size={18} weight="fill" className="text-brand-green shrink-0" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-5">
                            <Reveal delay={0.2}>
                                <img
                                    src="https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                    alt="Modern hospital building"
                                    loading="lazy"
                                    className="rounded-[1.75rem] w-full h-[520px] object-cover shadow-[0_24px_60px_rgb(10,37,64,0.15)]"
                                    data-testid="about-image"
                                />
                            </Reveal>
                        </div>
                    </div>
                </section>

                <section className="py-24 bg-brand-blue noise-overlay">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
                        <Reveal>
                            <h2 className="max-w-xl font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                                Let's build the future of cross-border healthcare together.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <Link
                                data-testid="about-partner-cta"
                                to="/partner"
                                className="inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 text-sm font-semibold text-white shadow-lg hover:bg-brand-green-light active:scale-95 transition-[background-color,transform] duration-200"
                            >
                                Partner With Us
                                <ArrowRight size={16} weight="bold" />
                            </Link>
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
